import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Invoice } from "../services/matchingService";

interface InvoiceCardProps {
  invoice: Invoice;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, invoice: Invoice) => void;
  selected?: boolean;
  onClick?: () => void;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  draggable = false,
  onDragStart,
  selected = false,
  onClick,
}) => {
  const getCategoriaColor = (categoria: string) => {
    switch (categoria) {
      case "despesa":
        return "error";
      case "despesa_a_faturar":
        return "warning";
      case "regular":
        return "default";
      case "a_faturar":
        return "info";
      default:
        return "default";
    }
  };

  const getTipoLabel = (tipo?: string) => {
    switch (tipo) {
      case "refeicao":
        return "Refeição";
      case "representacao":
        return "Representação";
      case "deslocacao":
        return "Deslocação";
      default:
        return tipo || "N/A";
    }
  };

  return (
    <Card
      draggable={draggable}
      onDragStart={(e) => onDragStart && onDragStart(e, invoice)}
      onClick={onClick}
      sx={{
        cursor: draggable ? "grab" : onClick ? "pointer" : "default",
        border: selected ? "2px solid" : "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected ? "action.selected" : "background.paper",
        "&:hover": {
          boxShadow: draggable || onClick ? 3 : 1,
        },
        "&:active": {
          cursor: draggable ? "grabbing" : "default",
        },
      }}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" component="div">
              €{Number(invoice.valor).toFixed(2)}
            </Typography>
          </Box>
          {draggable && (
            <Tooltip title="Arrasta para criar match">
              <IconButton size="small">
                <DragIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {invoice.numero_fatura}
        </Typography>

        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {dayjs(invoice.data_emissao).format("DD/MM/YYYY")}
        </Typography>

        <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
          <Chip
            label={invoice.categoria}
            color={getCategoriaColor(invoice.categoria)}
            size="small"
          />
          {invoice.tipo && (
            <Chip
              label={getTipoLabel(invoice.tipo)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {invoice.nif_emitente && (
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            NIF: {invoice.nif_emitente}
          </Typography>
        )}

        {invoice.descricao && (
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 0.5 }}
            noWrap
          >
            {invoice.descricao}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoiceCard;
